import { isNil } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useGetProfileQuery,
  useResetPasswordMutation,
  useUpdatePasswordMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import { Button } from '../../components/Button/Button';
import { EyeIcon } from '../../components/Icons';
import { BaseLayout } from '../../components/Layout/Layout';
import { TextField } from '../../components/TextField/TextField';
import { logout } from '../../helpers/auth';
import { useBack } from '../../hooks/useBack';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useLanguage } from '../../locale/useLanguage';
import { AuthProvider } from '../../models/auth.models';
import { Routes } from '../../routes/types';

import './ChangePasswordView.css';
import { ValidationRow } from './components/ValidationRow';

type ChangePasswordViewProps = {};

export const ChangePasswordView: React.FC<ChangePasswordViewProps> = () => {
  const [t] = useLanguage();
  const dispatch = useDispatch();
  const goBack = useBack();
  const location = useLocation();
  const history = useHistory();
  const queryParams = useQueryParams();

  const code = queryParams.get('code');
  const email = queryParams.get('email');

  const isRecovery = location.pathname === Routes.RecoverPasswordChangePassword;

  useEffect(() => {
    if (isRecovery && (!code || !email)) {
      history.replace(Routes.Home);
    }
  }, [code, email, history, isRecovery]);

  const title = isRecovery
    ? t('pageTitles.editPasswordPasswordRecovery')
    : t('pageTitles.changePassword');

  const [password, setPassword] = useState<string>('');
  const [oldPassword, setOldPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [hasLetter, setHasLetter] = useState<boolean>(false);
  const [hasDigit, setHasDigit] = useState<boolean>(false);
  const [hasSymbol, setHasSymbol] = useState<boolean>(false);
  const [hasRightLength, setHasRightLength] = useState<boolean>(false);
  const [isConfirmEqual, setIsConfirmEqual] = useState<boolean>();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const { data: profile } = useGetProfileQuery(undefined, {
    skip: isRecovery,
  });

  const [resetPassword] = useResetPasswordMutation();
  const [updatePasword] = useUpdatePasswordMutation();

  useEffect(() => {
    // Check password length
    if (password.length > 8 && password.length < 16) {
      setHasRightLength(true);
    } else {
      setHasRightLength(false);
    }

    // Check symbols
    if (new RegExp(/^.*[!"£$%&/()=?;:@#.,\-°[\]{}*]+.*$/).test(password)) {
      setHasSymbol(true);
    } else {
      setHasSymbol(false);
    }

    // Check digit
    if (new RegExp(/^.*\d+.*$/).test(password)) {
      setHasDigit(true);
    } else {
      setHasDigit(false);
    }

    // Check letter
    if (new RegExp(/^.*[a-zA-Z]+.*$/).test(password)) {
      setHasLetter(true);
    } else {
      setHasLetter(false);
    }
  }, [password]);

  useEffect(() => {
    if (confirmPassword) {
      if (password === confirmPassword) {
        setIsConfirmEqual(true);
      } else {
        setIsConfirmEqual(false);
      }
    }
  }, [confirmPassword, password]);

  useEffect(() => {
    if (!isNil(profile)) {
      const isLocalAuth = profile?.providers.includes(AuthProvider.Password);
      if (!isLocalAuth) {
        history.push(Routes.Home);
      }
    }
  }, [history, profile]);

  const isButtonDisabled =
    !hasSymbol ||
    !hasLetter ||
    !hasDigit ||
    !hasRightLength ||
    !isConfirmEqual ||
    (!isRecovery && !oldPassword);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleShowOldPassword = useCallback(() => {
    setShowOldPassword((prev) => !prev);
  }, []);

  const recoverPassword = useCallback(() => {
    resetPassword({ newPassword: password, email: email!, code: code! }).then(
      (res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          logout(dispatch);
          history.replace(Routes.ChangePasswordSuccess, {
            isFromChangePassword: true,
          });
        }

        if (errorRes.error) {
          // TODO: Handle errors
        }
      }
    );
  }, [code, dispatch, email, history, password, resetPassword]);

  const updateUserPassword = useCallback(() => {
    updatePasword({ oldPassword, newPassword: password }).then((res) => {
      const { successRes, errorRes } = parseRes(res);
      if (successRes.data) {
        logout(dispatch);
        history.replace(Routes.ChangePasswordSuccess, {
          isFromChangePassword: true,
        });
      }

      if (errorRes.error) {
        // TODO: Handle errors
      }
    });
  }, [dispatch, history, oldPassword, password, updatePasword]);

  const changePassword = useCallback(() => {
    if (isRecovery) {
      recoverPassword();
    } else {
      updateUserPassword();
    }
  }, [isRecovery, recoverPassword, updateUserPassword]);

  return (
    <BaseLayout hasBack onBackClick={goBack} title={title}>
      <h2 className="page-title">{t('changePassword.title')}</h2>
      <p className="page-description">{t('changePassword.description')}</p>
      {!isRecovery && (
        <div className="change-password--field">
          <TextField
            placeholder={t('changePassword.oldPasswordPlaceholder')}
            name="current-password"
            autocomplete="current-password"
            onValueChange={setOldPassword}
            value={oldPassword}
            type={showOldPassword ? undefined : 'password'}
            Icon={EyeIcon}
            onIconClick={toggleShowOldPassword}
          />
        </div>
      )}
      <div className="change-password--field">
        <TextField
          placeholder={t('changePassword.newPasswordPlaceholder')}
          name="password"
          autocomplete="new-password"
          onValueChange={setPassword}
          value={password}
          type={showPassword ? undefined : 'password'}
          Icon={EyeIcon}
          onIconClick={toggleShowPassword}
        />
      </div>
      <div className="change-password--field">
        <TextField
          placeholder={t('changePassword.confirmPasswordPlaceholder')}
          name="confirm-password"
          autocomplete="new-password"
          onValueChange={setConfirmPassword}
          value={confirmPassword}
          type={showConfirmPassword ? undefined : 'password'}
          Icon={EyeIcon}
          onIconClick={toggleShowConfirmPassword}
        />
      </div>
      <div className="change-password--validation-container">
        <ValidationRow
          label={t('changePassword.lengthValidator')}
          isValid={hasRightLength}
        />
        <div className="change-password--validation-row">
          <ValidationRow
            label={t('changePassword.atLeastOneNumberValidator')}
            isValid={hasDigit}
          />
        </div>
        <div className="change-password--validation-row">
          <ValidationRow
            label={t('changePassword.atLeastALetter')}
            isValid={hasLetter}
          />
        </div>
        <div className="change-password--validation-row">
          <ValidationRow
            label={t('changePassword.atLeastASymbol')}
            isValid={hasSymbol}
          />
        </div>
      </div>
      <div className="change-password--button-container">
        <Button
          onClick={changePassword}
          isDisabled={isButtonDisabled}
          isPrimary
          label={t('changePassword.confirm')}
        />
      </div>
    </BaseLayout>
  );
};
